import { useState, useEffect } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { useAccount } from 'wagmi';
import { IRelayPKP, AuthMethod, SessionSigs } from '@lit-protocol/types';
import { signOut as nextSignOut, getSession as getNextSession } from 'next-auth/react';
import { IExtendedRelayPKP } from '@shared/types/lit-protocol';
import { TDbEncryptedWallet } from '@shared/types/db/encrypted-wallets';
import { Keypair } from '@solana/web3.js';
import useOnlineStatus from '@rehooks/online-status';
import { toast } from 'sonner';
interface IPresentMFAInfo {
  authMethodType: number;
  authId: string;
  providerValue: string;
}
export interface IMFAInfo {
  providerValue?: string;
  authMethod?: AuthMethod;
  scopes?: boolean[];
  MFALoggedIn?: boolean;
  presentMFAInfo?: IPresentMFAInfo;
  sessionSigs?: SessionSigs;
}
export interface ILitSession {
  PKPNetwork: number;
  setPKPNetwork: (value: number) => void;
  connectedWalletAddress: string | null;
  userInfo: any;
  litSession: SessionSigs | null;
  PKP: IExtendedRelayPKP | null;
  allPKPs: IRelayPKP[];
  setAllPKPs: (value: IRelayPKP[]) => void;
  setLitSession: (value: any) => void;
  clearLitSession: (skipNextSignOut?: boolean) => void;
  setPKP: (value: IExtendedRelayPKP) => void;
  currentAuthMethod: AuthMethod | null;
  setCurrentAuthMethod: (value: AuthMethod) => void;
  MFAInfo: IMFAInfo | null;
  setMFAInfo: (value: any) => void;
  sessionLength: number;
  setSessionLength: (value: number) => void;
  setEmulationPKP: (value: IExtendedRelayPKP) => void;
  // Local storage user PKP when we are emulating
  userPKP: IExtendedRelayPKP | null;
  decryptedWallets: (TDbEncryptedWallet & {
    wallet: string | Keypair;
  })[] | null;
  setDecryptedWallets: (value: (TDbEncryptedWallet & {
    wallet: string | Keypair;
  })[] | null) => void;
}
export enum SESSION_LENGTHS {
  '10 minutes' = 600,
  '1 hour' = 3600,
  '6 hours' = 21600,
  '12 hours' = 43200,
  '1 day' = 86400,
  '3 days' = 259200,
  '1 week' = 604800,
}
export default function useLitSession(): ILitSession {
  const {
    address: connectedWalletAddress
  } = useAccount();
  const [network, setNetwork] = useState<number>(1);
  const [userInfo, setUserInfo] = useState(null);
  const [allPKPs, setAllPKPs] = useState([]);
  const [lsSessionValue, setlsSessionValue, removelsSessionValue] = useLocalStorage('litSession', null);
  const [lsMFAInfo, setlsMFAInfo, removelsMFAInfo] = useLocalStorage<IMFAInfo | null>('MFAInfo', null);
  const [lsPKPValue, setlsPKPValue] = useLocalStorage<IExtendedRelayPKP | null>('litPKP', null);
  const [emulationPKP, setEmulationPKP] = useLocalStorage<IExtendedRelayPKP | null>('emulationPKP', null);
  const [currentAuthMethod, setCurrentAuthMethod] = useLocalStorage<AuthMethod | null>('currentAuthMethod', null);
  const [sessionLength, setSessionLength] = useLocalStorage('sessionLength', SESSION_LENGTHS['1 week']);

  // TODO: if we want to add some more protection here vs just storing in local storage we should think about it.
  const [decryptedWallets, setDecryptedWallets] = useLocalStorage<(TDbEncryptedWallet & {
    wallet: string | Keypair;
  })[] | null>('decryptedWallets', null);
  const clearLitSession = (skipNextSignOut?: boolean) => {
    if (!skipNextSignOut) {
      nextSignOut();
    }
    removelsSessionValue();
    removelsMFAInfo();
    setlsPKPValue(null);
    setEmulationPKP(null);
    setUserInfo(null);
    setCurrentAuthMethod(null);
    setDecryptedWallets(null);

    // Clear specific lit keys here
    const litLocalKeys = ['lit-wallet-sig', 'lit-session-key'];
    // Genius-decryption-signature is deprecated
    const geniusLocalKeys = ['genius-lit-cached-min-sig', 'cipher1', 'cipher2', 'genius-decryption-signature', 'portfolioChartData', 'portfolioData', 'referralData', 'cloudUserData', 'capacityDelegationAuthSig', 'startDecryption', 'startTransactionConfirmationCheck', 'hasSentOTP', 'otp-stytchId', 'listedTokens', 'recent-wallets', 'disableRef', 'hasInitatedApple'];
    litLocalKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    geniusLocalKeys.forEach(key => {
      localStorage.removeItem(key);
    });
  };
  const onlineStatus = useOnlineStatus();

  //removing session if it has expired
  useEffect(() => {
    const checkNextSession = async () => {
      const nextSession = await getNextSession();
      if (!onlineStatus) {
        toast.error('You are offline, please check your internet connection');
        return;
      } else {
        if (!nextSession?.user) {
          global.missedSessions = global.missedSessions ? global.missedSessions + 1 : 1;
          if (global.missedSessions > 5) {
            clearLitSession();
            window.location.href = '/home?message=Your session has expired and you have been logged out to keep your account secure. Please log back in to continue.';
            return;
          }
          return;
        } else {
          global.missedSessions = 0;
        }
        return;
      }
    };
    if (lsSessionValue) {
      checkNextSession();
      try {
        const parsedSession: ILitSession = lsSessionValue;
        const expiry = new Date(JSON.parse(Object.values(parsedSession)?.[0]?.signedMessage)?.expiration);
        // Adding one hour buffer for session expiry
        const one_hour = 1000 * 60 * 60;
        if (expiry < new Date(new Date().getTime() - one_hour)) {
          console.log('Session expired, removing from local storage');
          clearLitSession();
          window.location.href = '/home?message=Your session has expired and you have been logged out to keep your account secure. Please log back in to continue.';
          return;
        }
      } catch (error) {
        console.error('Error parsing session from local storage:', error);
      }
    }
  }, [lsSessionValue]);

  // Write useEffect's to get all PKPs and PKP and Cloud Wallet from session

  return {
    PKPNetwork: network,
    setPKPNetwork: setNetwork,
    currentAuthMethod,
    setCurrentAuthMethod,
    connectedWalletAddress,
    userInfo,
    litSession: lsSessionValue,
    PKP: emulationPKP || lsPKPValue,
    setPKP: setlsPKPValue,
    allPKPs,
    setAllPKPs,
    setLitSession: setlsSessionValue,
    MFAInfo: lsMFAInfo,
    setMFAInfo: setlsMFAInfo,
    setEmulationPKP,
    sessionLength,
    setSessionLength,
    clearLitSession,
    decryptedWallets,
    setDecryptedWallets,
    userPKP: lsPKPValue
  };
}
export const getSessionExpiry = (session: SessionSigs): Date => {
  if (!session) return null;
  const expiry = new Date(JSON.parse(Object?.values(session)?.[0]?.signedMessage)?.expiration);
  return expiry;
};